import { DefaultOptionType } from "antd/es/select";
import { makeAutoObservable } from "mobx";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import {
  AbstractFilters,
  AbstractRow,
  RtKey,
} from "src/components/ReactiveTable/RtTypes";

type PropsRefSelectStore<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  tableStore: RtStore<TRow, TFilters>;
  initialOptions: DefaultOptionType[];
  renderLabel?: (row: TRow) => string;
  drawerTitle?: string;
};

export class RefSelectStore<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> {
  constructor(public props: PropsRefSelectStore<TRow, TFilters>) {
    makeAutoObservable(this);
  }

  /**
   * Нам необходимо отобразить выбранные опции при первой загрузке карточки
   * Мы не можем полагаться на данные tableStore.rows, так как они могут быть не актуальны
   * и мы не можем гарантировать, что они будут содержать все необходимые данные
   * поэтому мы используем initialOptions и selectedRows
   *
   * - initialOptions - это данные, которые мы получаем при первой загрузке карточки
   * - selectedRows - это данные, которые мы получаем при выборе строк в таблице и кешируем в этом сторе
   *
   * options - это объединенный массив с уникальными значениями из initialOptions и selectedRows
   */
  get options(): DefaultOptionType[] {
    const { tableStore, initialOptions } = this.props;
    const selectedOptions = tableStore.selectedRows.map((row) =>
      this.makeOption(tableStore.getRowKey(row), row),
    );
    const uniqueOptions = Array.from(
      new Map(
        [...initialOptions, ...selectedOptions].map((option) => [
          option.value,
          option,
        ]),
      ).values(),
    );
    return uniqueOptions;
  }

  makeOption = (key: RtKey, row?: TRow) => {
    const { tableStore, renderLabel, initialOptions } = this.props;
    if (row) {
      return {
        value: key,
        label: renderLabel?.(row) || tableStore.getRowKey(row),
      };
    }
    const fromInitial = initialOptions.find((option) => option.value === key);
    return {
      value: key,
      label: fromInitial?.label || String(key),
    };
  };
}
